import type {
	MockProfile,
	Occurrence,
	OccurrenceStatus,
} from '@/types/occurrence'
import { TYPES_WITH_MEDICAL, TYPES_WITH_PERSON } from '@/types/occurrence'
import { walkMatrix } from './matrix'

// ─── Permissões (protótipo da matriz da Seção 7) ────────────────────────────

export type Ability =
	| 'create'
	| 'edit'
	| 'confirmClassification'
	| 'concludeInvestigation'
	| 'approveAction'
	| 'manageActions'
	| 'viewMedical'
	| 'editMedical'
	| 'viewRestricted'
	| 'export'
	| 'close'
	| 'reopen'
	| 'cancel'

const ABILITIES: Record<MockProfile, Ability[]> = {
	ADMIN: ['export'],
	SSMA_MANAGER: [
		'create',
		'edit',
		'confirmClassification',
		'concludeInvestigation',
		'approveAction',
		'manageActions',
		'viewRestricted',
		'export',
		'close',
		'reopen',
		'cancel',
	],
	MANAGER: ['approveAction', 'manageActions', 'edit'],
	TECHNICIAN: ['create', 'edit', 'manageActions', 'viewRestricted'],
	MEDICINE: ['viewMedical', 'editMedical', 'export'],
}

export const can = (profile: MockProfile, ability: Ability) =>
	ABILITIES[profile].includes(ability)

/** Medicina só vê ocorrências com impacto médico. */
export function canSeeOccurrence(
	profile: MockProfile,
	o: Occurrence,
	_userName: string,
) {
	if (profile === 'MEDICINE') return TYPES_WITH_MEDICAL.includes(o.type)
	return true
}

// ─── Seções / navegação ─────────────────────────────────────────────────────

export type SectionKey =
	| 'summary'
	| 'data'
	| 'employee'
	| 'classification'
	| 'evidence'
	| 'testimonies'
	| 'committee'
	| 'timeline'
	| 'investigation'
	| 'matrix'
	| 'actions'
	| 'lesson'
	| 'medical'
	| 'documents'
	| 'closure'
	| 'audit'

export interface SectionDef {
	key: SectionKey
	label: string
	group:
		| 'Visão'
		| 'Cadastro'
		| 'Apuração'
		| 'Tratamento'
		| 'Restrito'
		| 'Governança'
}

export const SECTIONS: SectionDef[] = [
	{ key: 'summary', label: 'Resumo', group: 'Visão' },
	{ key: 'data', label: 'Dados da ocorrência', group: 'Cadastro' },
	{ key: 'employee', label: 'Colaborador', group: 'Cadastro' },
	{ key: 'classification', label: 'Classificação', group: 'Cadastro' },
	{ key: 'evidence', label: 'Evidências e fotos', group: 'Apuração' },
	{ key: 'testimonies', label: 'Depoimentos', group: 'Apuração' },
	{ key: 'committee', label: 'Comitê', group: 'Apuração' },
	{ key: 'timeline', label: 'Cronologia', group: 'Apuração' },
	{ key: 'investigation', label: 'Investigação de causas', group: 'Apuração' },
	{ key: 'matrix', label: 'Matriz investigativa', group: 'Apuração' },
	{ key: 'actions', label: 'Plano de ação', group: 'Tratamento' },
	{ key: 'lesson', label: 'Lição aprendida', group: 'Tratamento' },
	{ key: 'medical', label: 'Dados médicos / CAT', group: 'Restrito' },
	{ key: 'documents', label: 'Documentos', group: 'Governança' },
	{ key: 'closure', label: 'Encerramento', group: 'Governança' },
	{ key: 'audit', label: 'Auditoria', group: 'Governança' },
]

/** Exige investigação formal (comitê, causas, plano) — potenciais altos ou acidente real. */
export const needsInvestigation = (o: Occurrence) =>
	['N1', 'N2', 'N3'].includes(o.potential) ||
	o.type === 'WORK_ACCIDENT' ||
	o.type === 'OCCUPATIONAL_DISEASE'

/** O menu mostra somente os módulos aplicáveis ao tipo/classificação e visíveis ao perfil. */
export function visibleSections(
	o: Occurrence,
	profile: MockProfile,
): SectionKey[] {
	const hasPerson = TYPES_WITH_PERSON.includes(o.type)
	const hasMedical = TYPES_WITH_MEDICAL.includes(o.type)
	const result: SectionKey[] = ['summary']

	if (profile === 'MEDICINE') {
		return ['summary', 'medical', 'documents', 'audit'].filter(
			(k) => k !== 'medical' || hasMedical,
		) as SectionKey[]
	}

	result.push('data')
	if (hasPerson) result.push('employee')
	result.push('classification', 'evidence')
	if (profile !== 'MANAGER' && hasPerson) result.push('testimonies')
	if (needsInvestigation(o)) result.push('committee')
	result.push('timeline')
	if (needsInvestigation(o)) result.push('investigation')
	if (needsInvestigation(o) && hasPerson) result.push('matrix')
	result.push('actions')
	if (needsInvestigation(o)) result.push('lesson')
	// Gestor SSMA vê somente os dados administrativos da CAT; o clínico é exclusivo da Medicina
	if (hasMedical && (can(profile, 'viewMedical') || profile === 'SSMA_MANAGER'))
		result.push('medical')
	result.push('documents')
	result.push('closure', 'audit')
	return result
}

// ─── Progresso (Seção 13) ───────────────────────────────────────────────────

export interface ProgressStage {
	key: string
	label: string
	weight: number
	applicable: boolean
	done: boolean
	pending?: string
	section: SectionKey
}

export function progressStages(o: Occurrence): ProgressStage[] {
	const hasPerson = TYPES_WITH_PERSON.includes(o.type)
	const inv = needsInvestigation(o)
	const rootCauses = o.investigation.causes.filter((c) => c.isRoot)
	const regDone = Boolean(o.description && o.branch && o.date && o.contract)
	const empDone = Boolean(
		o.employee?.name && o.employee.badge && o.employee.jobRole,
	)
	const walk = walkMatrix(o.matrix.answers)
	const actionsDone =
		o.actions.length > 0 &&
		o.actions.every((a) => a.status === 'DONE' || a.status === 'CANCELLED')

	return [
		{
			key: 'reg',
			label: 'Cadastro inicial',
			weight: 15,
			applicable: true,
			done: regDone,
			pending: 'Preencher campos obrigatórios',
			section: 'data',
		},
		{
			key: 'cls',
			label: 'Classificação',
			weight: 10,
			applicable: true,
			done: Boolean(o.classification),
			pending: 'Classificação confirmada pelo Gestor SSMA',
			section: 'classification',
		},
		{
			key: 'emp',
			label: 'Dados do colaborador',
			weight: 10,
			applicable: hasPerson,
			done: empDone,
			pending: 'Informar nome, matrícula e função',
			section: 'employee',
		},
		{
			key: 'evi',
			label: 'Evidências e fotografias',
			weight: 10,
			applicable: true,
			done: o.evidences.filter((e) => !e.deletedReason).length > 0,
			pending: 'Anexar ao menos uma evidência',
			section: 'evidence',
		},
		{
			key: 'tes',
			label: 'Depoimentos',
			weight: 10,
			applicable: hasPerson,
			done: o.testimonies.length > 0,
			pending: 'Registrar depoimentos',
			section: 'testimonies',
		},
		{
			key: 'com',
			label: 'Comitê',
			weight: 5,
			applicable: inv,
			done: o.committee.length > 0,
			pending: 'Definir membros do comitê',
			section: 'committee',
		},
		{
			key: 'cau',
			label: 'Investigação de causas',
			weight: 15,
			applicable: inv,
			done: Boolean(o.investigation.method) && rootCauses.length > 0,
			pending: 'Escolher metodologia e identificar causa-raiz',
			section: 'investigation',
		},
		{
			key: 'mat',
			label: 'Matriz investigativa',
			weight: 10,
			applicable: inv && hasPerson,
			done: walk.done && Boolean(o.matrix.validatedBy),
			pending: 'Concluir e validar a matriz',
			section: 'matrix',
		},
		{
			key: 'act',
			label: 'Plano de ação',
			weight: 10,
			applicable: true,
			done: actionsDone,
			pending: 'Concluir todas as ações',
			section: 'actions',
		},
		{
			key: 'clo',
			label: 'Encerramento',
			weight: 5,
			applicable: true,
			done: o.status === 'CLOSED',
			pending: 'Encerrar a ocorrência',
			section: 'closure',
		},
	]
}

/** Pesos normalizados para 100% sobre as etapas aplicáveis. */
export function progressPercent(o: Occurrence): number {
	const stages = progressStages(o).filter((s) => s.applicable)
	const total = stages.reduce((sum, s) => sum + s.weight, 0)
	const done = stages
		.filter((s) => s.done)
		.reduce((sum, s) => sum + s.weight, 0)
	return total === 0 ? 0 : Math.round((done / total) * 100)
}

export const pendingStages = (o: Occurrence) =>
	progressStages(o).filter((s) => s.applicable && !s.done)

// ─── Transições de estado (Seção 2) ─────────────────────────────────────────

/** Pendência que impede uma transição, com a tela onde ela se resolve. */
export interface Blocker {
	text: string
	section?: SectionKey
}

const blk = (text: string, section?: SectionKey): Blocker => ({
	text,
	section,
})

export interface Transition {
	to: OccurrenceStatus
	label: string
	requiresJustification: boolean
	/** Perfil pode executar E não há pendências */
	allowed: boolean
	/** O perfil atual não tem permissão para esta transição */
	deniedByProfile: boolean
	/** Quem pode executar (texto para exibir ao usuário) */
	who: string
	blockers: Blocker[]
	variant: 'primary' | 'secondary' | 'danger'
}

export const isActionOverdue = (dueDate: string, status: string) =>
	['OPEN', 'IN_PROGRESS', 'REJECTED'].includes(status) &&
	dueDate < new Date().toISOString().slice(0, 10)

export function getTransitions(
	o: Occurrence,
	profile: MockProfile,
): Transition[] {
	const list: Transition[] = []
	const inv = needsInvestigation(o)
	const hasPerson = TYPES_WITH_PERSON.includes(o.type)
	const stages = progressStages(o)
	const stageDone = (key: string) =>
		stages.find((s) => s.key === key)?.done ?? true
	const roots = o.investigation.causes.filter((c) => c.isRoot)
	const walk = walkMatrix(o.matrix.answers)

	const mk = (
		to: OccurrenceStatus,
		label: string,
		allowedBy: boolean,
		blockers: Blocker[],
		opts: Partial<
			Pick<Transition, 'requiresJustification' | 'variant' | 'who'>
		> = {},
	) =>
		list.push({
			to,
			label,
			requiresJustification: opts.requiresJustification ?? false,
			variant: opts.variant ?? 'primary',
			who: opts.who ?? 'Gestor SSMA',
			deniedByProfile: !allowedBy,
			allowed: allowedBy && blockers.length === 0,
			blockers,
		})

	switch (o.status) {
		case 'DRAFT': {
			const b: Blocker[] = []
			if (!stageDone('reg')) b.push(blk('Preencher o cadastro inicial', 'data'))
			mk('REGISTERED', 'Registrar ocorrência', can(profile, 'create'), b, {
				who: 'Técnico ou Gestor SSMA',
			})
			break
		}
		case 'REGISTERED': {
			const b: Blocker[] = []
			if (!o.proposedClassification)
				b.push(blk('Propor uma classificação', 'classification'))
			if (hasPerson && !stageDone('emp'))
				b.push(blk('Informar os dados do colaborador', 'employee'))
			mk(
				'AWAITING_CLASSIFICATION',
				'Enviar para classificação',
				can(profile, 'edit'),
				b,
				{ who: 'Técnico, Gestor ou Gestor SSMA' },
			)
			break
		}
		case 'AWAITING_CLASSIFICATION': {
			const diverges = o.proposedClassification !== o.classification
			const b: Blocker[] = []
			if (!o.classification)
				b.push(blk('Confirmar a classificação', 'classification'))
			if (o.classification && diverges && !o.classificationDivergenceReason)
				b.push(
					blk(
						'Justificar a divergência da classificação proposta',
						'classification',
					),
				)
			mk(
				'INVESTIGATING',
				inv ? 'Confirmar e iniciar investigação' : 'Confirmar classificação',
				can(profile, 'confirmClassification'),
				b,
			)
			break
		}
		case 'INVESTIGATING':
		case 'REOPENED': {
			const b: Blocker[] = []
			if (inv) {
				if (!stageDone('com')) b.push(blk('Definir o comitê', 'committee'))
				if (!o.investigation.method)
					b.push(blk('Escolher a metodologia de investigação', 'investigation'))
				if (roots.length === 0)
					b.push(blk('Identificar ao menos uma causa-raiz', 'investigation'))
				if (hasPerson && !walk.done)
					b.push(blk('Concluir a matriz investigativa', 'matrix'))
			}
			mk(
				'AWAITING_VALIDATION',
				'Enviar para validação',
				can(profile, 'edit') && profile !== 'MANAGER',
				b,
				{ who: 'Técnico ou Gestor SSMA' },
			)
			break
		}
		case 'AWAITING_VALIDATION': {
			const b: Blocker[] = []
			if (inv && hasPerson && !o.matrix.validatedBy)
				b.push(blk('Validar a matriz investigativa (MI-12)', 'matrix'))
			mk(
				'ACTION_PLAN',
				'Validar investigação e liberar plano de ação',
				can(profile, 'concludeInvestigation'),
				b,
			)
			mk(
				'INVESTIGATING',
				'Devolver para investigação',
				can(profile, 'concludeInvestigation'),
				[],
				{ requiresJustification: true, variant: 'secondary' },
			)
			break
		}
		case 'ACTION_PLAN': {
			const b: Blocker[] = []
			if (o.actions.length === 0)
				b.push(blk('Cadastrar ao menos uma ação', 'actions'))
			const open = o.actions.filter(
				(a) => a.status !== 'DONE' && a.status !== 'CANCELLED',
			)
			if (open.length > 0)
				b.push(blk(`Concluir ${open.length} ação(ões) em aberto`, 'actions'))
			mk(
				'AWAITING_EFFICACY',
				'Avançar para avaliação de eficácia',
				can(profile, 'concludeInvestigation'),
				b,
			)
			break
		}
		case 'AWAITING_EFFICACY': {
			const b: Blocker[] = []
			const missing = o.actions.filter(
				(a) => a.status === 'DONE' && !a.efficacy,
			)
			if (missing.length > 0)
				b.push(
					blk(`Avaliar a eficácia de ${missing.length} ação(ões)`, 'closure'),
				)
			if (!o.closure.note.trim())
				b.push(blk('Registrar a decisão de encerramento', 'closure'))
			mk('CLOSED', 'Encerrar ocorrência', can(profile, 'close'), b)
			mk(
				'ACTION_PLAN',
				'Retornar ao plano de ação',
				can(profile, 'concludeInvestigation'),
				[],
				{ requiresJustification: true, variant: 'secondary' },
			)
			break
		}
		case 'CLOSED':
			mk('REOPENED', 'Reabrir ocorrência', can(profile, 'reopen'), [], {
				requiresJustification: true,
				variant: 'secondary',
			})
			break
		default:
			break
	}

	if (!['CLOSED', 'CANCELLED', 'DRAFT'].includes(o.status)) {
		mk('CANCELLED', 'Cancelar ocorrência', can(profile, 'cancel'), [], {
			requiresJustification: true,
			variant: 'danger',
		})
	}
	return list
}

/** Campos críticos bloqueados após validação da investigação. */
export const isLockedAfterValidation = (o: Occurrence) =>
	['ACTION_PLAN', 'AWAITING_EFFICACY', 'CLOSED', 'CANCELLED'].includes(o.status)

/** Evidências restritas só aparecem para quem pode ver conteúdo restrito (Medicina vê as clínicas). */
export const canViewConfidentiality = (
	profile: MockProfile,
	c: 'PUBLIC' | 'INTERNAL' | 'RESTRICTED',
) =>
	c !== 'RESTRICTED' || can(profile, 'viewRestricted') || profile === 'MEDICINE'
